import type { Editor } from '@tiptap/core';
import { store } from '@/state/store';
import type { MarkupView } from '@/state/types';
import { trackChangesKey } from './trackChanges';

// ═══════════════════════════════════════════════════════════════
// Správa sledovaných změn (Fáze 3) — přijímání a odmítání.
//
// Sémantika (shodná s Wordem):
//   Přijmout vložení  → text zůstane, značka se sundá
//   Odmítnout vložení → text se smaže
//   Přijmout smazání  → text se smaže doopravdy
//   Odmítnout smazání → text zůstane, značka se sundá
// ═══════════════════════════════════════════════════════════════

export type ChangeKind = 'insertion' | 'deletion';

export interface TrackedChange {
  kind: ChangeKind;
  /** Pozice v dokumentu (od–do). */
  from: number;
  to: number;
  text: string;
  author: string;
  date: string;
}

/** Je sledování změn zapnuté? */
export function isTrackingEnabled(): boolean {
  return !!store.get().doc.trackChanges;
}

/** Zapne/vypne sledování změn. */
export function setTracking(on: boolean): void {
  store.patchDoc({ trackChanges: on });
}

/** Aktuální režim zobrazení revizí (výchozí: všechny). */
export function getMarkupView(): MarkupView {
  return store.get().doc.markupView || 'all';
}

/**
 * Nastaví režim zobrazení revizí. Je to jen vizuální vrstva — dokument se
 * nemění, mění se pouze CSS třída na kořeni editoru (viz app.css).
 */
export function setMarkupView(view: MarkupView): void {
  store.patchDoc({ markupView: view });
  applyMarkupView(view);
}

/** Promítne režim do DOM (třída na .doc-page-content). */
export function applyMarkupView(view: MarkupView): void {
  const el = document.querySelector('.doc-page-content');
  if (!el) return;
  el.classList.remove('markup-all', 'markup-simple', 'markup-none', 'markup-original');
  el.classList.add('markup-' + view);
}

/** Popisky režimů pro UI. */
export const MARKUP_VIEW_LABELS: Record<MarkupView, string> = {
  all: 'Všechny revize',
  simple: 'Jednoduché revize',
  none: 'Bez revizí',
  original: 'Originál',
};

/**
 * Vypíše všechny sledované změny v dokumentu, seřazené podle pozice.
 * Sousední úseky se stejným typem, autorem a časem sloučí do jedné změny
 * (jinak by se každý znak jevil jako samostatná změna).
 */
export function listChanges(editor: Editor): TrackedChange[] {
  const out: TrackedChange[] = [];
  const { doc } = editor.state;

  doc.descendants((node, pos) => {
    if (!node.isText) return;
    for (const m of node.marks) {
      const kind = m.type.name;
      if (kind !== 'insertion' && kind !== 'deletion') continue;

      const change: TrackedChange = {
        kind,
        from: pos,
        to: pos + node.nodeSize,
        text: node.text || '',
        author: (m.attrs.author as string) || 'Neznámý autor',
        date: (m.attrs.date as string) || '',
      };

      // Sloučit se sousední změnou téhož druhu/autora, když navazuje.
      const prev = out[out.length - 1];
      if (
        prev &&
        prev.kind === change.kind &&
        prev.author === change.author &&
        prev.to === change.from
      ) {
        prev.to = change.to;
        prev.text += change.text;
      } else {
        out.push(change);
      }
    }
  });

  return out;
}

/** Přijme jednu změnu na dané pozici. */
export function acceptChange(editor: Editor, change: TrackedChange): void {
  const { state } = editor;
  const insType = state.schema.marks.insertion;
  const tr = state.tr;

  if (change.kind === 'insertion') {
    // Přijmout vložení → text zůstává, jen sundáme značku.
    tr.removeMark(change.from, change.to, insType);
  } else {
    // Přijmout smazání → text se smaže doopravdy.
    tr.delete(change.from, change.to);
  }
  // Označ transakci jako naši — jinak by ji sledování považovalo za
  // uživatelskou úpravu a mazání při přijímání by vytvořilo NOVOU změnu.
  tr.setMeta(trackChangesKey, true);
  editor.view.dispatch(tr);
}

/** Odmítne jednu změnu na dané pozici. */
export function rejectChange(editor: Editor, change: TrackedChange): void {
  const { state } = editor;
  const delType = state.schema.marks.deletion;
  const tr = state.tr;

  if (change.kind === 'insertion') {
    // Odmítnout vložení → text se smaže.
    tr.delete(change.from, change.to);
  } else {
    // Odmítnout smazání → text zůstává, sundáme značku.
    tr.removeMark(change.from, change.to, delType);
  }
  tr.setMeta(trackChangesKey, true);
  editor.view.dispatch(tr);
}

/**
 * Přijme všechny změny. Zpracováváme od konce dokumentu, aby se pozice
 * dřívějších změn neposouvaly mazáním těch pozdějších.
 */
export function acceptAllChanges(editor: Editor): number {
  const changes = listChanges(editor).reverse();
  if (!changes.length) return 0;

  const { state } = editor;
  const insType = state.schema.marks.insertion;
  const tr = state.tr;

  for (const c of changes) {
    if (c.kind === 'insertion') {
      tr.removeMark(c.from, c.to, insType);
    } else {
      tr.delete(c.from, c.to);
    }
  }
  tr.setMeta(trackChangesKey, true);
  editor.view.dispatch(tr);
  return changes.length;
}

/** Odmítne všechny změny (od konce, ze stejného důvodu jako accept). */
export function rejectAllChanges(editor: Editor): number {
  const changes = listChanges(editor).reverse();
  if (!changes.length) return 0;

  const { state } = editor;
  const delType = state.schema.marks.deletion;
  const tr = state.tr;

  for (const c of changes) {
    if (c.kind === 'insertion') {
      tr.delete(c.from, c.to);
    } else {
      tr.removeMark(c.from, c.to, delType);
    }
  }
  tr.setMeta(trackChangesKey, true);
  editor.view.dispatch(tr);
  return changes.length;
}

/** Počet sledovaných změn (pro UI). */
export function changeCount(editor: Editor): number {
  return listChanges(editor).length;
}

/**
 * Najde změnu pod kurzorem (nebo v aktuálním výběru). Používá ribbon pro
 * tlačítka Přijmout/Odmítnout — ve Wordu fungují taky na aktuální pozici.
 */
export function changeAtSelection(editor: Editor): TrackedChange | null {
  const { from, to } = editor.state.selection;
  const changes = listChanges(editor);
  // Změna, která se překrývá s výběrem (nebo obsahuje kurzor).
  return changes.find((c) => c.from < to && c.to > from) || null;
}

/**
 * Přijme změnu pod kurzorem a skočí na další (jako ve Wordu).
 * Vrací true, když se něco přijalo.
 */
export function acceptAtSelection(editor: Editor): boolean {
  const c = changeAtSelection(editor);
  if (!c) return false;
  acceptChange(editor, c);
  goToNextChange(editor);
  return true;
}

/** Odmítne změnu pod kurzorem a skočí na další. */
export function rejectAtSelection(editor: Editor): boolean {
  const c = changeAtSelection(editor);
  if (!c) return false;
  rejectChange(editor, c);
  goToNextChange(editor);
  return true;
}

/**
 * Skočí na následující změnu za kurzorem (jako „Next" ve Wordu).
 * Když už žádná není, vrátí se na první — Word to dělá stejně (cyklí).
 * Vrací true, když se někam skočilo.
 */
export function goToNextChange(editor: Editor): boolean {
  const changes = listChanges(editor);
  if (!changes.length) return false;

  const pos = editor.state.selection.to;
  const next = changes.find((c) => c.from > pos) || changes[0];
  focusChange(editor, next);
  return true;
}

/** Skočí na předchozí změnu před kurzorem (jako „Previous" ve Wordu). */
export function goToPrevChange(editor: Editor): boolean {
  const changes = listChanges(editor);
  if (!changes.length) return false;

  const pos = editor.state.selection.from;
  const before = changes.filter((c) => c.to < pos);
  const prev = before.length ? before[before.length - 1] : changes[changes.length - 1];
  focusChange(editor, prev);
  return true;
}

/** Vybere změnu v textu a odscrolluje na ni. */
function focusChange(editor: Editor, c: TrackedChange): void {
  editor.chain().focus().setTextSelection({ from: c.from, to: c.to }).run();
  // Odscrolluj na výběr — ProseMirror to sám nedělá spolehlivě.
  // domAtPos může vrátit textový uzel (bez scrollIntoView) nebo nic, proto
  // dohledáme nejbližší element a metodu ověříme.
  try {
    const at = editor.view.domAtPos(c.from);
    const node = at?.node as any;
    const el: any = node?.nodeType === 1 ? node : node?.parentElement;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  } catch {
    // Scroll je jen pohodlí — když selže, výběr už je nastavený.
  }
}
